import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Button,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Pagination,
  Divider,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import CallIcon from "@mui/icons-material/Call";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchCompanies,
  fetchCompanyStatuses,
  updateCompany,
  setQuery,
} from "./companiesSlice";
import CompanyFormDialog from "./CompanyFormDialog";
import {
  readFavorites,
  writeFavorites,
  isFavorite,
  toggleFavorite,
} from "./favorites";

const telHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

const StatusPill = ({ status }) => {
  const map = {
    live: { color: "#107e3e", bg: "#e8f5ec" },
    production: { color: "#0a6ed1", bg: "#e8f0fa" },
    testing: { color: "#df6e0c", bg: "#fdf2e6" },
    development: { color: "#6a6d70", bg: "#f1f1f1" },
    compliant: { color: "#107e3e", bg: "#e8f5ec" },
    removed: { color: "#bb0000", bg: "#fdecec" },
    delete: { color: "#bb0000", bg: "#fdecec" },
    problem: { color: "#bb0000", bg: "#fdecec" },
    "block admin": { color: "#6a6d70", bg: "#f1f1f1" },
    "district admin": { color: "#6a6d70", bg: "#f1f1f1" },
  };
  const s = map[status] || map.development;
  return (
    <Box
      sx={{
        display: "inline-block",
        backgroundColor: s.bg,
        color: s.color,
        px: 0.75,
        py: 0.125,
        borderRadius: "2px",
        fontSize: "0.6875rem",
        fontWeight: 600,
      }}
    >
      {status || "—"}
    </Box>
  );
};

// Inline editable status — click pill → menu of statuses → save without
// leaving the list page. Stops click propagation so the row/card onClick
// (which opens the company detail page) does not fire.
const StatusEditor = ({ c, statuses, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const open = Boolean(anchor);

  const openMenu = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (saving) return;
    setAnchor(e.currentTarget);
  };
  const closeMenu = (e) => {
    e?.stopPropagation?.();
    setAnchor(null);
  };
  const pickStatus = async (e, next) => {
    e.stopPropagation();
    setAnchor(null);
    if (next === c.status) return;
    setSaving(true);
    try {
      await onChange(c, next);
    } finally {
      setSaving(false);
    }
  };

  const list =
    statuses && statuses.length
      ? statuses
      : ["live", "production", "testing", "development", "removed"];

  return (
    <>
      <Box
        component="span"
        onClick={openMenu}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          cursor: saving ? "wait" : "pointer",
          borderRadius: "2px",
          opacity: saving ? 0.6 : 1,
          transition: "opacity .12s ease",
          "&:hover": saving ? {} : { boxShadow: "0 0 0 2px rgba(13,148,136,0.18)" },
        }}
      >
        <StatusPill status={c.status} />
        {saving ? (
          <CircularProgress size={10} sx={{ ml: 0.25 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: "text.secondary" }} />
        )}
      </Box>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 0.5, minWidth: 160 } }}
      >
        {list.map((s) => (
          <MenuItem
            key={s}
            selected={s === c.status}
            onClick={(e) => pickStatus(e, s)}
            sx={{ fontSize: "0.8125rem", py: 0.5 }}
          >
            <StatusPill status={s} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Click-to-toggle star — yellow when favorited, outline when not.
// stopPropagation so it doesn't trigger the card's navigate-to-detail click.
const FavoriteStar = ({ active, onToggle }) => (
  <Tooltip title={active ? "Remove from favorites" : "Add to favorites"}>
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{
        color: active ? "#f59e0b" : "text.disabled",
        "&:hover": { color: active ? "#d97706" : "#f59e0b" },
        p: 0.5,
      }}
    >
      {active ? <StarIcon sx={{ fontSize: 24 }} /> : <StarBorderIcon sx={{ fontSize: 24 }} />}
    </IconButton>
  </Tooltip>
);

// Each contact role gets its own dot colour so operator / electrician / secretary
// are visually distinguishable at a glance, even in a long list of cards.
const CONTACT_COLORS = {
  operator:    { idle: "#a78bfa", hover: "#8b5cf6" }, // violet
  electrician: { idle: "#fbbf24", hover: "#f59e0b" }, // amber
  secretary:   { idle: "#34d399", hover: "#10b981" }, // mint/teal
};

// Compact 3-row contact block used inside the Companies table's Contact
// column. Each row is rendered as a single line — `📞 label-name-number` —
// which packs more horizontally than the old multi-column grid.
//
// Whole row is a tel: link when a number exists; stopPropagation keeps the
// surrounding TableRow's onClick (open edit) from firing on dial.
const TableContactCell = ({ c }) => {
  const rows = [
    { label: "Operator",    name: c.operator_name,     phone: c.operator_phone,           colors: CONTACT_COLORS.operator },
    { label: "Electrician", name: null,                phone: c.local_electrician_number, colors: CONTACT_COLORS.electrician },
    { label: "Secretary",   name: c.secretary_name,    phone: c.secretary_phone,          colors: CONTACT_COLORS.secretary },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      {rows.map((r) => {
        const callable = Boolean(r.phone);
        return (
          <Box
            key={r.label}
            onClick={callable ? (e) => e.stopPropagation() : undefined}
            component={callable ? "a" : "div"}
            href={callable ? telHref(r.phone) : undefined}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              borderRadius: "4px",
              py: 0.25,
              minWidth: 0,
              textDecoration: "none",
              color: "inherit",
              ...(callable && {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "& .row-call-btn": { backgroundColor: r.colors.hover },
                },
              }),
            }}
          >
            <Box
              className="row-call-btn"
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.125,
                backgroundColor: callable ? r.colors.idle : "rgba(0,0,0,0.08)",
                transition: "background-color 0.15s",
                "& .MuiSvgIcon-root": {
                  color: callable ? "#ffffff" : "rgba(0,0,0,0.3)",
                  fontSize: 11,
                },
              }}
            >
              <CallIcon />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                lineHeight: 1.35,
                // Wrap rather than truncate so long names + numbers stay
                // fully visible (was hiding e.g. "PRIYA PURASI SECURITY").
                whiteSpace: "normal",
                wordBreak: "break-word",
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
                {r.label}
              </Box>
              <Box component="span" sx={{ color: r.name ? "text.primary" : "text.disabled", mx: 0.25 }}>
                -{r.name || "—"}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 500,
                  color: r.phone ? "text.primary" : "text.disabled",
                }}
              >
                -{r.phone || "—"}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const CompanyCard = ({ c, onEdit, statuses, onChangeStatus, favActive, onToggleFav }) => (
  <Paper
    variant="outlined"
    onClick={() => onEdit(c)}
    sx={{
      p: 1.25,
      cursor: "pointer",
      minWidth: 0,
      overflow: "hidden",
      // Light blue-grey surface — matches the Search page mobile cards.
      backgroundColor: "#f4f8fc",
      borderColor: "#dbe6f0",
      transition: "background-color 0.15s, border-color 0.15s, box-shadow 0.15s",
      "&:hover": {
        backgroundColor: "#e8f0fa",
        borderColor: "primary.main",
        boxShadow: (t) => t.customShadows?.md,
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0, flex: 1, mr: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            color: "text.primary",
            fontSize: "0.6875rem",
            flexShrink: 0,
          }}
        >
          #{c.manufacturing_id ?? "—"}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            wordBreak: "break-word",
            minWidth: 0,
          }}
        >
          {c.name || "—"}
        </Typography>
      </Stack>
      <FavoriteStar active={favActive} onToggle={onToggleFav} />
    </Stack>

    <Divider sx={{ my: 0.75 }} />

    <Stack spacing={0.4}>
      {/* Address row — always rendered so all cards have the same height */}
      {(() => {
        const addressParts = [c.district, c.block, c.panchayat].filter(Boolean);
        const hasAddress = addressParts.length > 0;
        return (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <PlaceIcon
              sx={{
                fontSize: 13,
                color: hasAddress ? "text.primary" : "text.disabled",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: hasAddress ? "text.primary" : "text.disabled",
                fontStyle: hasAddress ? "normal" : "italic",
              }}
            >
              {hasAddress ? addressParts.join(" · ") : "No address"}
            </Typography>
          </Stack>
        );
      })()}
      {/* Same compact contact block as the desktop table — keeps fonts and
          icon sizes consistent across views. */}
      <TableContactCell c={c} />
    </Stack>
  </Paper>
);

export default function CompaniesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, pagination, statuses, query, loading } = useSelector(
    (s) => s.companies
  );
  const currentUser = useSelector((s) => s.auth.user);
  const userKey = currentUser?._id || currentUser?.username || "anon";

  const [searchInput, setSearchInput] = useState(query.search);
  const [openForm, setOpenForm] = useState(false);
  const [favorites, setFavorites] = useState(() => readFavorites(userKey));
  // `view` now lives in redux query so it survives navigation
  const view = query.view || "all";
  const debounceRef = useRef(null);

  // Infinite scroll (mobile only)
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // Re-load favorites if the logged-in admin changes
  useEffect(() => {
    setFavorites(readFavorites(userKey));
  }, [userKey]);

  useEffect(() => {
    dispatch(fetchCompanyStatuses());
  }, [dispatch]);

  useEffect(() => {
    const { view: _v, ...rest } = query;
    const params = { ...rest };
    if (view === "favorites") {
      params.favoriteIds = favorites.join(",");
      // Favorites view ignores status — show every starred company
      // regardless of its current status (live, removed, etc.)
      delete params.status;
    }
    dispatch(fetchCompanies(params));
  }, [dispatch, query, view, favorites]);

  const onToggleFavorite = (company) => {
    const id = String(company._id);
    const next = toggleFavorite(favorites, id);
    setFavorites(next);
    writeFavorites(userKey, next);
  };

  // Infinite scroll loader — fetches the next page and appends to items.
  // Triggered by the IntersectionObserver sentinel at the bottom of the
  // mobile card list.
  const loadMoreRef = useRef();
  loadMoreRef.current = async () => {
    if (loadingMore || loading) return;
    if (pagination.page >= pagination.pages) return; // no more pages
    setLoadingMore(true);
    const { view: _v, ...rest } = query;
    const params = { ...rest, page: pagination.page + 1, append: true };
    if (view === "favorites") {
      params.favoriteIds = favorites.join(",");
      delete params.status;
    }
    try {
      await dispatch(fetchCompanies(params));
    } finally {
      setLoadingMore(false);
    }
  };

  // Observe the bottom sentinel to auto-load more on mobile only
  useEffect(() => {
    if (!isMobile) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current?.();
      },
      { rootMargin: "200px" } // start loading slightly before reaching bottom
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, items.length, pagination.page, pagination.pages]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const next = searchInput.trim();
    if (next === query.search) return;
    debounceRef.current = setTimeout(() => {
      dispatch(setQuery({ search: next, page: 1 }));
    }, 1000);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [searchInput]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    dispatch(setQuery({ search: searchInput.trim(), page: 1 }));
  };

  const onClearSearch = () => {
    setSearchInput("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    dispatch(setQuery({ search: "", page: 1 }));
  };

  const onCreate = () => {
    setOpenForm(true);
  };

  const onEdit = (row) => {
    navigate(`/companies/${row._id}/details`);
  };

  const onChangeStatus = async (row, next) => {
    const res = await dispatch(
      updateCompany({ id: row._id, payload: { status: next } })
    );
    if (updateCompany.fulfilled.match(res)) {
      toast.success(`Status set to ${next}`);
      dispatch(fetchCompanies(query));
    } else {
      toast.error(res.payload || "Failed to update status");
    }
  };

  const headers = useMemo(
    () => [
      { key: "manufacturing_id", label: "Mfg ID", w: 90 },
      { key: "name", label: "Name", w: 200 },
      { key: "contact", label: "Contact", w: 280 },
      { key: "status", label: "Status", w: 130 },
      { key: "district", label: "District" },
      { key: "block", label: "Block" },
      { key: "fav", label: "Fav", w: 60, align: "center" },
      { key: "actions", label: "Actions", w: 90, align: "right" },
    ],
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Inherit MainLayout's flex column with overflow: hidden so the
        // Paper below can flex to fill the remaining viewport vertically.
        flex: { md: 1 },
        minHeight: 0,
        height: { xs: "auto", md: "auto" },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        mb={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Breadcrumbs
            separator="›"
            sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
          >
            <MuiLink
              underline="hover"
              color="text.secondary"
              href="#"
              sx={{ fontSize: "0.9375rem" }}
            >
              Home
            </MuiLink>
            <Typography
              color="text.primary"
              sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
            >
              Companies
            </Typography>
          </Breadcrumbs>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => dispatch(fetchCompanies(query))}
              sx={{ display: { xs: "none", md: "inline-flex" } }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            New Company
          </Button>
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          flex: { md: 1 },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
            borderBottom: "1px solid",
            borderColor: "#a7f3d0",
            backgroundColor: "#f0fdfa",
            flexShrink: 0,
          }}
        >
          <Box
            component="form"
            onSubmit={onSearchSubmit}
            sx={{
              display: "flex",
              gap: 1,
              flex: { xs: "1 1 100%", md: "1 1 auto" },
              minWidth: 0,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={isMobile ? "Search…" : "Search by name, phone, district, mfg id, motor IMEI, OHT IMEI…"}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={onClearSearch} sx={{ minWidth: 0, p: 0.25 }}>
                      Clear
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            {/* Mobile-only "+" button — paired with search at the right */}
            <Button
              variant="contained"
              onClick={onCreate}
              sx={{
                display: { xs: "inline-flex", sm: "none" },
                minWidth: 44,
                px: 0,
                flexShrink: 0,
              }}
            >
              <AddIcon fontSize="small" />
            </Button>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flex: { xs: "1 1 100%", md: "0 0 auto" }, flexWrap: { xs: "wrap", md: "nowrap" } }}>
            <TextField
              select
              size="small"
              label="View"
              value={view}
              onChange={(e) =>
                dispatch(setQuery({ view: e.target.value, page: 1 }))
              }
              sx={{ flex: 1, minWidth: 130 }}
            >
              <MenuItem value="all" sx={{ fontSize: "0.8125rem" }}>
                All
              </MenuItem>
              <MenuItem value="favorites" sx={{ fontSize: "0.8125rem" }}>
                <StarIcon sx={{ fontSize: 14, color: "#f59e0b", mr: 0.5 }} />
                Favorites
              </MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={query.status}
              onChange={(e) => dispatch(setQuery({ status: e.target.value, page: 1 }))}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 110 }}
            >
              <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: "0.8125rem" }}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Size"
              value={query.limit}
              onChange={(e) => dispatch(setQuery({ limit: Number(e.target.value), page: 1 }))}
              sx={{ width: 80, display: { xs: "none", sm: "inline-flex" } }}
            >
              {[10, 25, 50, 100].map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: "0.8125rem" }}>{n}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        {/* Pagination — desktop / tablet only. Mobile uses infinite scroll. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.75}
          sx={{
            p: 1,
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            display: { xs: "none", sm: "flex" },
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: "center", sm: "left" } }}>
            Showing {items.length} of {pagination.total} · Page {pagination.page} of {Math.max(pagination.pages, 1)}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              size="small"
              page={pagination.page}
              count={Math.max(pagination.pages, 1)}
              onChange={(_, p) => dispatch(setQuery({ page: p }))}
              shape="rounded"
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
            />
          </Box>
        </Stack>

        {isMobile ? (
          <Box sx={{ p: 1 }}>
            {loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                  No companies found.
                </Typography>
                <MuiLink component="button" onClick={onCreate} sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  Create the first one
                </MuiLink>
              </Box>
            )}
            <Stack spacing={1}>
              {items.map((c) => (
                <CompanyCard
                  key={c._id}
                  c={c}
                  onEdit={onEdit}
                  statuses={statuses}
                  onChangeStatus={onChangeStatus}
                  favActive={isFavorite(favorites, c._id)}
                  onToggleFav={() => onToggleFavorite(c)}
                />
              ))}
            </Stack>

            {/* Infinite-scroll footer: sentinel + status text */}
            {items.length > 0 && (
              <Box
                ref={sentinelRef}
                sx={{
                  py: 2,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                {loadingMore ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading more…</Typography>
                  </Stack>
                ) : pagination.page < pagination.pages ? (
                  <Typography variant="caption">Scroll for more</Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    All {pagination.total} companies loaded
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                // Fixed layout so every column has identical width on every
                // row — the only way the inner grid in the Contact cell lines
                // up vertically across rows.
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  py: 1.75,
                  px: 2,
                },
                // Ensure the last data row also has a bottom divider so the
                // table doesn't look "open" when there are few rows.
                "& tbody tr:last-child .MuiTableCell-root": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell
                      key={h.key}
                      align={h.align}
                      sx={{ width: h.w, whiteSpace: "nowrap" }}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={headers.length} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={headers.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No companies found.{" "}
                      <MuiLink component="button" onClick={onCreate}>
                        Create the first one
                      </MuiLink>
                    </TableCell>
                  </TableRow>
                )}
                {items.map((c) => (
                  <TableRow
                    key={c._id}
                    hover
                    onClick={() => onEdit(c)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ fontFamily: "monospace", color: "text.primary" }}>
                      {c.manufacturing_id ?? "—"}
                    </TableCell>
                    <TableCell>
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(c);
                        }}
                        underline="hover"
                        sx={{
                          textAlign: "left",
                          fontWeight: 700,
                          fontSize: "inherit",
                          color: "text.primary",
                        }}
                      >
                        {c.name || "—"}
                      </MuiLink>
                    </TableCell>
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      sx={{ verticalAlign: "middle" }}
                    >
                      <TableContactCell c={c} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <StatusEditor c={c} statuses={statuses} onChange={onChangeStatus} />
                    </TableCell>
                    <TableCell>{c.district || "—"}</TableCell>
                    <TableCell>{c.block || "—"}</TableCell>
                    <TableCell
                      align="center"
                      sx={{ p: 0.25 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FavoriteStar
                        active={isFavorite(favorites, c._id)}
                        onToggle={() => onToggleFavorite(c)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => onEdit(c)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <CompanyFormDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
      />
    </Box>
  );
}
